import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { pagesData } from '../data/pagesContent';

export default function QuotePage() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');
  const pageKey = isEn ? 'en-get-a-quote' : 'tilbud';
  const page = pagesData[pageKey];

  const [formData, setFormData] = useState({
    website_url: '',
    navn: '',
    telefon: '',
    email: '',
    adresse: '',
    emne: isEn ? 'Plumbing work' : 'VVS-arbejde',
    beskrivelse: '',
    tidshorisont: isEn ? 'As soon as possible' : 'Hurtigst muligt'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(isEn ? 'Failed to send quote request. Please try calling us directly.' : 'Kunne ikke indsende tilbudsforespørgsel. Prøv venligst at ringe til os.');
      }

      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err.message || (isEn ? 'An error occurred. Please try again.' : 'Der opstod en fejl. Prøv venligst igen.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <html lang={page.lang} />
        <title>{page.title}</title>
        <meta name="description" content={page.meta.description} />
        <meta property="og:title" content={page.meta.ogTitle} />
        <meta property="og:description" content={page.meta.ogDescription} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/hero.jpg" />
        <link rel="canonical" href={`https://billundvvs.netlify.app${isEn ? '/en/get-a-quote/' : '/tilbud/'}`} />
      </Helmet>

      <div className="bg-slate-50 border-b border-slate-200 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            {isEn ? 'Get a no-obligation quote' : 'Få et uforpligtende tilbud'}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            {isEn
              ? 'Tell us briefly about the job and we will call you back within 24 hours to arrange a free site visit. You always get a fixed price – and the quote is 100 % without obligation.'
              : 'Fortæl os kort om opgaven, så ringer vi tilbage inden for 24 timer og aftaler en gratis besigtigelse. Du får altid en fast pris – og tilbuddet er 100 % uforpligtende.'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {submitSuccess ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center my-8 shadow-xs">
            <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl">
              <i className="fa-solid fa-check"></i>
            </div>
            <h2 className="text-2xl font-bold text-green-900 mb-2">
              {isEn ? 'Thank you for your enquiry!' : 'Tak for din henvendelse!'}
            </h2>
            <p className="text-sm text-green-800 leading-relaxed max-w-lg mx-auto">
              {isEn
                ? 'We have received your details and will call you back within 24 hours on working days.'
                : 'Vi har modtaget dine oplysninger og ringer tilbage inden for 24 timer på hverdage.'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-6 sm:p-10 shadow-sm space-y-6">
            {/* Honeypot field */}
            <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
              <label htmlFor="website_url">Don’t fill this out:</label>
              <input
                type="text"
                id="website_url"
                name="website_url"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website_url}
                onChange={handleChange}
              />
            </div>

            {submitError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-xs sm:text-sm text-red-700">
                {submitError}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  {isEn ? 'Name' : 'Navn'} *
                </label>
                <input
                  type="text"
                  required
                  name="navn"
                  value={formData.navn}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  {isEn ? 'Phone' : 'Telefon'} *
                </label>
                <input
                  type="tel"
                  required
                  name="telefon"
                  value={formData.telefon}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  {isEn ? 'Email' : 'E-mail'} *
                </label>
                <input
                  type="email"
                  required
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  {isEn ? 'Address' : 'Adresse'}
                </label>
                <input
                  type="text"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                {isEn ? 'What is it about?' : 'Hvad drejer det sig om?'}
              </label>
              <select
                name="emne"
                value={formData.emne}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
              >
                {isEn ? (
                  <>
                    <option value="Plumbing work">Plumbing work</option>
                    <option value="Bathroom">Bathroom</option>
                    <option value="Heat pump">Heat pump</option>
                    <option value="District heating">District heating</option>
                    <option value="Heating system / boiler replacement">Heating system / boiler replacement</option>
                    <option value="Sheet metal / gutters">Sheet metal / gutters</option>
                    <option value="Ventilation">Ventilation</option>
                    <option value="New construction">New construction</option>
                    <option value="Commercial / service agreement">Commercial / service agreement</option>
                    <option value="Other">Other</option>
                  </>
                ) : (
                  <>
                    <option value="VVS-arbejde">VVS-arbejde</option>
                    <option value="Badeværelse">Badeværelse</option>
                    <option value="Varmepumpe">Varmepumpe</option>
                    <option value="Fjernvarme">Fjernvarme</option>
                    <option value="Varmeanlæg / udskiftning af fyr">Varmeanlæg / udskiftning af fyr</option>
                    <option value="Blikkenslager / tagrender">Blikkenslager / tagrender</option>
                    <option value="Ventilation">Ventilation</option>
                    <option value="Nybyggeri">Nybyggeri</option>
                    <option value="Erhverv / serviceaftale">Erhverv / serviceaftale</option>
                    <option value="Andet">Andet</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                {isEn ? 'Describe the job' : 'Beskriv opgaven'}
              </label>
              <textarea
                rows={4}
                name="beskrivelse"
                value={formData.beskrivelse}
                onChange={handleChange}
                placeholder={isEn ? "Tell us about the project..." : "Fortæl lidt om opgaven..."}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
              ></textarea>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                {isEn ? 'When should the work be done?' : 'Hvornår skal opgaven udføres?'}
              </label>
              <select
                name="tidshorisont"
                value={formData.tidshorisont}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-md text-sm text-slate-900 focus:bg-white focus:outline-hidden focus:border-[#d3102e] focus:ring-1 focus:ring-[#d3102e]"
              >
                {isEn ? (
                  <>
                    <option value="As soon as possible">As soon as possible</option>
                    <option value="Within 1 month">Within 1 month</option>
                    <option value="Within 3 months">Within 3 months</option>
                    <option value="I am just comparing prices">I am just comparing prices</option>
                  </>
                ) : (
                  <>
                    <option value="Hurtigst muligt">Hurtigst muligt</option>
                    <option value="Inden for 1 måned">Inden for 1 måned</option>
                    <option value="Inden for 3 måneder">Inden for 3 måneder</option>
                    <option value="Jeg undersøger bare priser">Jeg undersøger bare priser</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-[#d3102e] hover:bg-[#a50d26] disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider px-8 py-3.5 rounded-md transition-colors inline-flex items-center justify-center gap-2"
              >
                <span>{isSubmitting ? (isEn ? 'Sending...' : 'Sender...') : (isEn ? 'Send and get a quote →' : 'Send og få tilbud →')}</span>
              </button>
            </div>

            <p className="text-xs text-slate-500 pt-2">
              {isEn ? (
                <>
                  Free and without obligation. We never pass on your details.{' '}
                  <Link to="/en/privacy-policy" className="text-[#d3102e] hover:underline">
                    Privacy policy
                  </Link>
                  .
                </>
              ) : (
                <>
                  Gratis og uforpligtende. Vi videregiver aldrig dine oplysninger.{' '}
                  <Link to="/privatlivspolitik" className="text-[#d3102e] hover:underline">
                    Privatlivspolitik
                  </Link>
                  .
                </>
              )}
            </p>
          </form>
        )}

        {/* Section: Sådan foregår det */}
        <div className="mt-16 pt-12 border-t border-slate-200">
          <h2 className="text-2xl font-extrabold text-slate-900 mb-8">
            {isEn ? 'How it works' : 'Sådan foregår det'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(isEn
              ? [
                  {
                    num: '1',
                    title: 'We call you',
                    desc: 'Within 24 hours on working days – and arrange a time for a site visit.'
                  },
                  {
                    num: '2',
                    title: 'Free site visit',
                    desc: 'We come out, look at the job and advise on solutions and materials.'
                  },
                  {
                    num: '3',
                    title: 'Written fixed-price quote',
                    desc: 'No hidden costs. Travel within Billund Municipality is included.'
                  },
                  {
                    num: '4',
                    title: 'You decide',
                    desc: 'The quote is without obligation. If you say yes, we agree a date.'
                  }
                ]
              : [
                  {
                    num: '1',
                    title: 'Vi ringer dig op',
                    desc: 'Inden for 24 timer på hverdage – og aftaler et tidspunkt for besigtigelse.'
                  },
                  {
                    num: '2',
                    title: 'Gratis besigtigelse',
                    desc: 'Vi kommer ud, ser på opgaven og rådgiver om løsninger og materialer.'
                  },
                  {
                    num: '3',
                    title: 'Skriftligt tilbud med fast pris',
                    desc: 'Ingen skjulte omkostninger. Kørsel i Billund Kommune er inkluderet.'
                  },
                  {
                    num: '4',
                    title: 'Du bestemmer',
                    desc: 'Tilbuddet er uforpligtende. Siger du ja, aftaler vi en dato.'
                  }
                ]
            ).map((step) => (
              <div key={step.num} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#d3102e] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {step.num}
                </div>
                <div>
                  <h3 className="font-bold text-sm text-slate-900 leading-snug">{step.title}</h3>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
            {isEn ? (
              <>
                <strong>Is it urgent?</strong> Call us directly on{' '}
                <a href="tel:+4575331276" className="text-[#d3102e] font-bold hover:underline">
                  +45 75 33 12 76
                </a>
                . For emergencies we are{' '}
                <Link to="/en/plumbing/emergency" className="text-[#d3102e] font-bold hover:underline">
                  on call 24/7
                </Link>
                .
              </>
            ) : (
              <>
                <strong>Haster det?</strong> Ring direkte på{' '}
                <a href="tel:+4575331276" className="text-[#d3102e] font-bold hover:underline">
                  75 33 12 76
                </a>
                . Ved akutte skader har vi{' '}
                <Link to="/vvs/akut-vvs" className="text-[#d3102e] font-bold hover:underline">
                  døgnvagt
                </Link>
                .
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
